import React from 'react';
import { Box, Typography, Skeleton, Alert } from '@mui/material';
import { KeyFindingsGrid } from './KeyFindingsGrid';
import type { MarketReportKeyFinding } from '@horwath-htl/shared-types';

export interface KeyFindingsSectionProps {
  reportId: number;
  marketName: string;
  reportMonth?: string;
  variant?: 'grid' | 'list';
  showViewToggle?: boolean;
  className?: string;
  findings?: MarketReportKeyFinding[];
  loading?: boolean;
  error?: Error | null;
}

export const KeyFindingsSection: React.FC<KeyFindingsSectionProps> = ({
  reportId,
  marketName,
  reportMonth,
  variant = 'grid',
  showViewToggle = true,
  className,
  findings,
  loading = false,
  error = null
}) => {

  if (loading) {
    return <KeyFindingsSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load key findings. Please try again later.
      </Alert>
    );
  }

  if (!findings || findings.length === 0) {
    return (
      <Box className={className}>
        <Typography variant="body1" color="text.secondary">
          No key findings available for this report.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2">
          Key Findings - {marketName}
          {reportMonth && (
            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
              {formatReportMonth(reportMonth)}
            </Typography>
          )}
        </Typography>
      </Box>

      <KeyFindingsGrid findings={findings} />
    </Box>
  );
};

const KeyFindingsSkeleton: React.FC = () => {
  const count = 6;
  
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 2 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={200} />
      ))}
    </Box>
  );
};

function formatReportMonth(month: string): string {
  const year = month.substring(0, 4);
  const monthNum = month.substring(4, 6);
  const date = new Date(parseInt(year), parseInt(monthNum) - 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
}